import { useCallback, useEffect, useState } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'

import { AppShell } from './app/AppShell'
import { LoginPage } from './app/LoginPage'
import { EmployeesPage } from './features/hr/EmployeesPage'
import { SecurityPage } from './features/settings/SecurityPage'
import { EmptyState } from './components/ui/EmptyState'
import { fetchMe, logout, restoreSession, type CurrentUser } from './lib/api'

type AuthState =
  | { status: 'restoring' }
  | { status: 'anonymous' }
  | { status: 'authenticated'; user: CurrentUser }

export function App() {
  const [auth, setAuth] = useState<AuthState>({ status: 'restoring' })

  // The access token lives in memory only, so a page refresh always starts
  // signed out. The HttpOnly refresh cookie is what makes that invisible:
  // exchange it for a fresh access token before rendering anything.
  useEffect(() => {
    let cancelled = false
    restoreSession().then((user) => {
      if (cancelled) return
      setAuth(user ? { status: 'authenticated', user } : { status: 'anonymous' })
    })
    return () => {
      cancelled = true
    }
  }, [])

  const handleSignedIn = useCallback(async () => {
    const user = await fetchMe()
    setAuth({ status: 'authenticated', user })
  }, [])

  const handleSignOut = useCallback(async () => {
    await logout()
    setAuth({ status: 'anonymous' })
  }, [])

  if (auth.status === 'restoring') {
    // Deliberately plain. A full-page skeleton for a request that usually
    // takes 80ms is a flash of loading state that reads as slowness.
    return (
      <div className="lg-auth" role="status" aria-live="polite">
        <span className="lg-sr-only">Restoring your session</span>
      </div>
    )
  }

  if (auth.status === 'anonymous') {
    return <LoginPage onSignedIn={handleSignedIn} />
  }

  const { user } = auth

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<AppShell user={user} onSignOut={handleSignOut} />}>
          <Route index element={<Navigate to="/hr/employees" replace />} />
          <Route path="/hr/employees" element={<EmployeesPage permissions={user.permissions} />} />
          {/* No permission gate: everyone manages their own second factor,
              and the endpoints behind it only ever act on the caller. */}
          <Route path="/settings/security" element={<SecurityPage />} />
          <Route
            path="*"
            element={
              <EmptyState
                title="Not built yet"
                body="This module is scaffolded but not implemented. HR is the reference vertical; Finance, Supply Chain, and CRM follow the same shape."
              />
            }
          />
        </Route>
      </Routes>
    </BrowserRouter>
  )
}
